import React from "react";
import { useQuery } from "@tanstack/react-query";
import api from "../../../utils/api";
import { Building, FileText, Clock, CheckCircle, AlertCircle } from "lucide-react";

const ParalegalDashboard = () => {
    const { data, isLoading, error } = useQuery({
        queryKey: ["paralegal-dashboard"],
        queryFn: async () => {
            const token = localStorage.getItem("auth_token");
            if (!token) throw new Error("Authentication required");

            const res = await api.get("/paralegals/dashboard", {
                headers: { Authorization: `Bearer ${token}` },
            });
            return res.data.data;
        },
        staleTime: 1000 * 60 * 2,
    });

    // Loading State
    if (isLoading) {
        return (
            <div className="flex justify-center items-center py-16">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                <p className="ml-3 text-gray-600">Loading your dashboard...</p>
            </div>
        );
    }

    // Error State
    if (error || !data) {
        return (
            <div className="text-center py-12 bg-red-50 rounded-lg">
                <AlertCircle className="w-12 h-12 text-red-600 mx-auto mb-3" />
                <p className="text-red-600 font-medium">
                    {error?.message || "Failed to load dashboard. Please try again."}
                </p>
                <button
                    onClick={() => window.location.reload()}
                    className="mt-3 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                >
                    Retry
                </button>
            </div>
        );
    }

    const { office, supervising_lawyer, stats, recent_requests } = data;

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h2 className="text-2xl font-bold text-gray-900">Paralegal Dashboard</h2>
                    <p className="text-gray-600 mt-1">
                        Welcome back, <strong>{supervising_lawyer.name}</strong>
                    </p>
                </div>
                <div className="text-right">
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                        <Building className="w-4 h-4" />
                        <span>{office.name}</span>
                    </div>
                </div>
            </div>

            {/* Stats Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <StatCard
                    icon={<FileText className="w-6 h-6 text-blue-600" />}
                    label="Total Assigned"
                    value={stats.total_assigned}
                    color="blue"
                />
                <StatCard
                    icon={<Clock className="w-6 h-6 text-yellow-600" />}
                    label="Pending"
                    value={stats.pending}
                    color="yellow"
                />
                <StatCard
                    icon={<AlertCircle className="w-6 h-6 text-orange-600" />}
                    label="In Progress"
                    value={stats.in_progress}
                    color="orange"
                />
                <StatCard
                    icon={<CheckCircle className="w-6 h-6 text-green-600" />}
                    label="Completed"
                    value={stats.completed}
                    color="green"
                />
            </div>

            {/* Supervising Lawyer */}
            <div className="bg-white border border-gray-200 rounded-lg p-5">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center text-white font-bold">
                            {supervising_lawyer.name.charAt(0)}
                        </div>
                        <div>
                            <h3 className="font-semibold text-gray-900">Supervising Lawyer</h3>
                            <p className="text-sm text-gray-600">{supervising_lawyer.name}</p>
                            <p className="text-xs text-gray-500">{supervising_lawyer.email}</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Recent Requests */}
            <div className="bg-white border border-gray-200 rounded-lg p-5">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Requests</h3>
                {recent_requests.length === 0 ? (
                    <p className="text-center text-gray-500 py-8">No recent requests assigned yet.</p>
                ) : (
                    <div className="space-y-3">
                        {recent_requests.map((req, index) => (
                            <div
                                key={index}
                                className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                            >
                                <div>
                                    <p className="font-medium text-gray-800">{req.title}</p>
                                    <p className="text-sm text-gray-600">Client: {req.client_name}</p>
                                </div>
                                <span
                                    className={`px-3 py-1 rounded-full text-xs font-medium ${req.status === "pending"
                                        ? "bg-yellow-100 text-yellow-800"
                                        : req.status === "in_progress"
                                            ? "bg-blue-100 text-blue-800"
                                            : "bg-green-100 text-green-800"
                                        }`}
                                >
                                    {req.status.replace("_", " ")}
                                </span>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

// Reusable Stat Card
const StatCard = ({ icon, label, value, color }) => {
    const colorClasses = {
        blue: "bg-blue-50 text-blue-700 border-blue-200",
        yellow: "bg-yellow-50 text-yellow-700 border-yellow-200",
        orange: "bg-orange-50 text-orange-700 border-orange-200",
        green: "bg-green-50 text-green-700 border-green-200",
    };

    return (
        <div className={`bg-white border ${colorClasses[color]} rounded-lg p-5`}>
            <div className="flex items-center justify-between">
                <div>
                    <p className="text-sm font-medium text-gray-600">{label}</p>
                    <p className="text-3xl font-bold mt-1">{value}</p>
                </div>
                <div className="p-3 bg-white rounded-lg shadow-sm">{icon}</div>
            </div>
        </div>
    );
};

export default ParalegalDashboard;